/**
 * O bloco "Preços" da aba Agendamento do painel (#1085).
 *
 * O atendente responde "quanto custa?" sem sair da conversa: busca a consulta,
 * o exame ou o procedimento, vê o preço particular e o copia para a caixa de
 * mensagem, sem enviar. Os preços vêm do sistema da clínica toda madrugada
 * (`GET /v1/consultas/precos`, #1084); aqui ninguém cadastra preço.
 *
 * Sempre o preço particular, como referência, mesmo para quem tem convênio
 * (decisão de 26/10): por isso não há seletor de convênio.
 *
 * Desenho no modelo validado: `docs/design/prototipos/agenda-csr/produtos-e-precos.html`.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PrecoDaClinica, TipoDePrecoDaApi } from '@cliqcrm/contracts';
import { chaves } from '@/shared/api/chaves';
import { useApi, usePrecosDaClinica } from '@/shared/api/consultas';
import { recadoDeErro } from '@/shared/api/recado-de-erro';
import { abertaPorExtenso, dinheiroComCentavos } from '@/shared/lib/formato';
import { Carregando } from '@/shared/ui/estados/estados';
import { Botao, Campo, Icone, Paginacao, Segmentado } from '@/shared/ui';
import { direcaoDaTroca, textoDoPreco, type DirecaoDaTroca } from '../lib/precos';
import './precos-da-clinica.css';

type Filtro = 'todos' | TipoDePrecoDaApi;

const FILTROS: { valor: Filtro; rotulo: string }[] = [
  { valor: 'todos', rotulo: 'Todos' },
  { valor: 'consulta', rotulo: 'Consultas' },
  { valor: 'exame', rotulo: 'Exames' },
  { valor: 'procedimento', rotulo: 'Procedimentos' },
];

/** A pausa da busca: filtra ao digitar, sem uma leitura por letra. */
const PAUSA_DA_BUSCA_MS = 300;

/** A ordem das listas no filtro, para a troca deslizar para o lado certo. */
const ORDEM_DOS_FILTROS: readonly Filtro[] = FILTROS.map((opcao) => opcao.valor);

/** Quanto o ✓ do "Copiar" fica à vista. */
const COPIADO_MS = 2_000;

export function PrecosDaClinica({
  podeAtualizar,
  aoCopiar,
}: {
  /** Quem edita a configuração da organização vê "Atualizar agora". */
  podeAtualizar: boolean;
  /** Põe o texto na caixa de mensagem da conversa aberta, sem enviar. */
  aoCopiar: (texto: string) => void;
}): ReactNode {
  const [filtro, definirFiltro] = useState<Filtro>('todos');
  const [digitado, definirDigitado] = useState('');
  const [busca, definirBusca] = useState('');
  const [pagina, definirPagina] = useState(1);
  const [copiado, definirCopiado] = useState<{ id: string; texto: string } | null>(null);

  useEffect(() => {
    const espera = setTimeout(() => {
      definirBusca(digitado.trim());
      // Busca nova começa da primeira página: a 5 da busca anterior pode nem existir.
      definirPagina(1);
    }, PAUSA_DA_BUSCA_MS);
    return () => clearTimeout(espera);
  }, [digitado]);

  useEffect(() => {
    if (!copiado) return;
    const espera = setTimeout(() => definirCopiado(null), COPIADO_MS);
    return () => clearTimeout(espera);
  }, [copiado]);

  const precos = usePrecosDaClinica(
    { tipo: filtro === 'todos' ? null : filtro, busca, pagina },
    true,
  );
  const atualizar = useAtualizarPrecos();

  const lista = precos.data;

  /*
   * A lista que está à vista, com a direção da última troca. Só muda quando a
   * resposta nova chega (e não com a anterior ainda segurada no lugar): assim
   * o deslize acontece uma vez, com o conteúdo certo.
   */
  const chave = `${filtro}|${busca}|${pagina}`;
  const [vista, definirVista] = useState<{
    readonly chave: string;
    readonly filtro: Filtro;
    readonly pagina: number;
    readonly direcao: DirecaoDaTroca | null;
  }>({ chave, filtro, pagina, direcao: null });
  const chegou = lista !== undefined && !precos.isPlaceholderData;
  useEffect(() => {
    if (!chegou) return;
    definirVista((anterior) =>
      anterior.chave === chave
        ? anterior
        : {
            chave,
            filtro,
            pagina,
            direcao: direcaoDaTroca(ORDEM_DOS_FILTROS, anterior, { filtro, pagina }),
          },
    );
  }, [chegou, chave, filtro, pagina]);
  const nuncaAtualizado = lista !== undefined && lista.sincronizado_em === null;
  const totalDePaginas = lista ? Math.max(1, Math.ceil(lista.total / lista.por_pagina)) : 1;

  const copiar = (preco: PrecoDaClinica) => {
    const texto = textoDoPreco(preco);
    aoCopiar(texto);
    definirCopiado({ id: preco.id, texto });
  };

  const rodape = (
    <div className="precos-da-clinica__rodape">
      <span>
        {lista?.sincronizado_em ? `Atualizado ${abertaPorExtenso(lista.sincronizado_em)}` : ''}
      </span>
      {podeAtualizar && (
        <Botao
          variante="fantasma"
          miudo
          onClick={() => atualizar.mutate()}
          desabilitado={atualizar.isPending}
          motivo="Atualizando os preços…"
        >
          Atualizar agora
        </Botao>
      )}
    </div>
  );

  const avisoDaAtualizacao = atualizar.isPending ? (
    <p className="precos-da-clinica__aviso" role="status">
      Atualizando os preços… pode levar um minuto.
    </p>
  ) : atualizar.isError ? (
    <p className="precos-da-clinica__aviso precos-da-clinica__aviso--erro" role="alert">
      {recadoDeErro(atualizar.error, {
        generica: 'Não deu para atualizar agora. Os preços abaixo continuam os de antes.',
      })}
    </p>
  ) : null;

  if (nuncaAtualizado) {
    return (
      <div className="precos-da-clinica">
        <div className="precos-da-clinica__vazio">
          <strong>Os preços ainda não foram atualizados.</strong>
          Eles vêm do sistema da clínica toda madrugada.
        </div>
        {avisoDaAtualizacao}
        {rodape}
      </div>
    );
  }

  return (
    <div className="precos-da-clinica">
      <Campo
        type="search"
        icone="lupa"
        rotuloOculto="Buscar preço"
        placeholder="Nome ou código"
        maxLength={100}
        value={digitado}
        onChange={(evento) => definirDigitado(evento.target.value)}
      />
      <div className="precos-da-clinica__filtro">
        <Segmentado
          como="grupo"
          rotulo="Tipo de preço"
          escolhida={filtro}
          aoEscolher={(escolhido) => {
            definirFiltro(escolhido);
            definirPagina(1);
          }}
          opcoes={FILTROS}
        />
      </div>

      {precos.isPending ? (
        <Carregando forma="linha" linhas={3} rotulo="Carregando os preços" />
      ) : precos.isError || (lista && lista.itens.length === 0) ? (
        /*
         * A busca que falha mostra o mesmo "Nada encontrado" da vazia, sem o
         * erro grande com código de suporte (27/10): num painel estreito ele
         * tomava o bloco inteiro por causa de uma busca. "Tentar de novo"
         * continua ali, discreto, para a falha passageira.
         */
        <div className="precos-da-clinica__vazio" role="status">
          <strong>Nada encontrado.</strong>
          {precos.isError ? (
            <Botao variante="fantasma" miudo onClick={() => void precos.refetch()}>
              Tentar de novo
            </Botao>
          ) : busca ? (
            'Confira a grafia ou busque pelo código.'
          ) : (
            'Escolha outro tipo acima.'
          )}
        </div>
      ) : (
        lista && (
          <>
            <ul
              key={vista.chave}
              className={
                vista.direcao
                  ? `precos-da-clinica__lista precos-da-clinica__lista--de-${vista.direcao}`
                  : 'precos-da-clinica__lista'
              }
              aria-label="Preços"
              aria-busy={precos.isPlaceholderData ? true : undefined}
            >
              {lista.itens.map((preco) => (
                <LinhaDoPreco
                  key={preco.id}
                  preco={preco}
                  copiado={copiado?.id === preco.id}
                  aoCopiar={() => copiar(preco)}
                />
              ))}
            </ul>
            <Paginacao
              pagina={lista.pagina}
              totalDePaginas={totalDePaginas}
              aoIr={definirPagina}
              rotulo="Páginas dos preços"
            />
          </>
        )
      )}

      {copiado && (
        <p className="precos-da-clinica__aviso precos-da-clinica__aviso--ok" role="status">
          Copiado para a mensagem: “{copiado.texto}”
        </p>
      )}
      {avisoDaAtualizacao}
      <p className="precos-da-clinica__nota">
        Preço particular, de referência. Vem do sistema da clínica.
      </p>
      {rodape}
    </div>
  );
}

function LinhaDoPreco({
  preco,
  copiado,
  aoCopiar,
}: {
  preco: PrecoDaClinica;
  copiado: boolean;
  aoCopiar: () => void;
}): ReactNode {
  const padrao = preco.tipo === 'consulta' && preco.medico === null;
  return (
    <li className="precos-da-clinica__item">
      <div className="precos-da-clinica__texto">
        <span className="precos-da-clinica__nome">{preco.nome}</span>
        {preco.medico && <span className="precos-da-clinica__medico">{preco.medico.nome}</span>}
        {padrao && (
          <span className="precos-da-clinica__medico">
            Sem preço próprio do médico
            <span className="precos-da-clinica__padrao">padrão</span>
          </span>
        )}
        <span className="precos-da-clinica__apoio">
          <span className="precos-da-clinica__codigo">{preco.codigo_procedimento}</span>
          {preco.grupo && ` · ${preco.grupo}`}
        </span>
      </div>
      <span className="precos-da-clinica__valor">{dinheiroComCentavos(preco.preco)}</span>
      <button
        type="button"
        className="precos-da-clinica__copiar"
        data-copiado={copiado ? 'sim' : undefined}
        aria-label={
          copiado ? `${preco.nome} copiado para a mensagem` : `Copiar ${preco.nome} para a mensagem`
        }
        title={copiado ? 'Copiado' : 'Copiar para a mensagem'}
        onClick={aoCopiar}
      >
        <Icone nome={copiado ? 'check' : 'copiar'} />
      </button>
    </li>
  );
}

/** "Atualizar agora": relê o sistema da clínica e aplica; só esta tela faz a ação. */
function useAtualizarPrecos() {
  const api = useApi();
  const cache = useQueryClient();
  return useMutation({
    mutationFn: () => api!.sincronizarPrecosDaClinica(),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: chaves.precosDaClinica() });
    },
  });
}
